import LayerGroup from 'ol/layer/Group';
import BaseLayer from 'ol/layer/Base';

import BaseTileLayer from 'ol/layer/BaseTile';
import BaseImageLayer from 'ol/layer/BaseImage';
import GeomodImageLayer from './GeomodImageLayer';
import GeomodTileLayer from './GeomodTileLayer';
import GeomodWebGlTileLayer from './GeomodWebGlTileLayer';
import { DEFAULT_LIGHT_CONDITION } from '../sources/GeomodSourceUtils';
import GeomodMergedLayer from './GeomodMergedLayer';

/**
 * @typedef {import("./GeomodTileLayer").GeomodTileLayerAdditionnalOptions|import("./GeomodImageLayer").GeomodImageLayerAdditionnalOptions} LayerParameter
 */

/**
 * @typedef {Object} SpecificOptions
 * @property {string} [defaultUrl] The default url of the layers.
 * @property {Array<LayerParameter>|Array<BaseLayer>} [layers] The layers to add in the group.
 */

/**
 * @typedef {import("ol/layer/Group").Options|SpecificOptions} GeomodLayerGroupOptions
 */

/**
 * Group for geomod map. It can create group from data object and can sort its layers
 * in group of groups.
 * @extends {LayerGroup}
 */
export default class GeomodLayerGroup extends LayerGroup {
  /**
     * @constructor
     * @param {GeomodLayerGroupOptions} options The layer group options.
     */
  constructor(options = {}) {
    const opts = { ...options };
    if (options.layers !== undefined && !(options.layers[0] instanceof BaseLayer)) {
      opts.layers = createLayers(options.layers);
    }

    super(opts);
    this.on('change:layers', () => { this.sortedLayers_ = undefined; });

    /**
         * Create all layers corresponding to the parameters.
         * @param {Array<import("../GeomodMapParameters").LayerDeclaration>} paramOfLayers The array of layer parameters.
         * @return {Array<GeomodTileLayer|GeomodImageLayer>} The generated layers.
         */
    function createLayers(paramOfLayers) {
      const layers = [];

      // Init visible layers if it not exsits
      const visibleLayers = JSON.parse(localStorage.getItem('visibleLayers'));
      if (!visibleLayers || visibleLayers.length === 0) {
        const vl = [];
        for (const param of paramOfLayers) {
          if (param.visible === true) {
            vl.push(param.mergeId || param.name);
          }
        }
        localStorage.setItem('visibleLayers', JSON.stringify(Array.from(new Set(vl))));
      } else {
        const visibleBaseLayers = paramOfLayers.filter((lay) => visibleLayers.includes(lay.mergeId || lay.name) && lay.base);
        if (visibleBaseLayers.length === 0) {
          const firstBaseLayer = paramOfLayers.filter((lay) => lay.visible && lay.base)[0];
          if (firstBaseLayer) {
            localStorage.setItem('visibleLayers', JSON.stringify([firstBaseLayer.mergeId || firstBaseLayer.name, ...visibleLayers]));
          }
        }
      }
      for (const param of paramOfLayers) {
        layers.push(createLayer(param, opts.defaultUrl));
      }
      return layers;
    }

    /**
         * Create layer from a {@link LayerDeclaration}.
         * @param {import("../GeomodMapParameters").LayerDeclaration} layerParams The layer parameters.
         * @param {string} defaultUrlSource The url source of the layer.
         * @return {GeomodTileLayer|GeomodImageLayer} The generated layer.
         */
    function createLayer(layerParams, defaultUrlSource) {
      const params = { ...layerParams };
      if (!params.source && !defaultUrlSource) {
        throw new Error('No source available');
      }

      params.url = layerParams.source || defaultUrlSource;
      params.light = layerParams.light || DEFAULT_LIGHT_CONDITION;
      params.title = layerParams.groupName;
      params.additionals = { ...layerParams.additionals, merge: layerParams.mergeId };
      params.minZoom = layerParams.minZoom || 0
      params.maxZoom = layerParams.maxZoom || 30
      const visibleLayers = localStorage.getItem('visibleLayers')
      params.visible = visibleLayers.includes(layerParams.mergeId || layerParams.name);

      // Type = 'base' is used by openlayerSwitcher
      const layerType = params.type;
      if (params.base) {
        params.type = 'base';
      } else {
        params.type = undefined;
      }

      /** @type {GeomodTileLayer|GeomodImageLayer} */
      let layer;
      switch (layerType) {
        case 'TileWMS':
        case 'TileWMTS':
        case 'TileTMS':
          layer = new GeomodTileLayer({ ...params, sourceType: layerType });
          break;
        case 'ImageWMS':
        case 'TileRasterWMS':
          layer = new GeomodImageLayer({ ...params, sourceType: layerType });
          break;
        case 'WebGlTileWMS':
        case 'WebGlTileWMTS':
        case 'WebGlTileTMS':
          layer = new GeomodWebGlTileLayer({ ...params, sourceType: layerType });
          break;
        default:
          throw new Error(`Source type ${layerType} unrecognised.`);
      }
      return layer;
    }
  }

  /**
     * This method sort layers in group according to layers title.
     * @return {LayerGroup<LayerGroup>} A group which contains groups of layers.
     */
  getSortedLayers() {
    if (!this.sortedLayers_) {
      /** @type {Map<string,LayerToGroup>} */
      const groups = {};
      /** @type {Array<string>} */
      const groupsKeys = [];
      /** @type {string} */
      let titleGroup;
      for (const layer of this.getLayers().getArray()) {
        titleGroup = layer.getProperties().title;
        if (!groups[titleGroup]) {
          groups[titleGroup] = new LayerToGroup(titleGroup);
          groupsKeys.push(titleGroup);
        }
        groups[titleGroup].add(layer);
      }

      const sortedGroupArray = [];
      for (const key of groupsKeys) {
        sortedGroupArray.push(groups[key].createGroup());
      }

      this.sortedLayers_ = new LayerGroup({ layers: sortedGroupArray });
    }
    return this.sortedLayers_;
  }

  /**
     * Get the visible base layers of the group.
     * @returns {BaseLayer} The visible base layer of the group.
     */
  getVisibleLayerBase() {
    let layerBase;
    for (const layer of this.getBaseLayers()) {
      if (layer.getVisible()) {
        if (layer instanceof GeomodMergedLayer) {
          layerBase = layer.getSubLayers()[0];
        } else {
          layerBase = layer;
        }
        break;
      }
    }
    return layerBase;
  }

  /**
     * Get a list of base layer
     * @returns {Array<BaseLayer>} Liste of base layer.
     */
  getBaseLayers() {
    const baseLayers = [];
    let layer;
    for (const layerGroup of this.getSortedLayers().getLayersArray()) {
      layer = layerGroup.getLayersArray()[0];
      if (layer.get('type') === 'base') {
        baseLayers.push(layer);
      }
    }
    return baseLayers;
  }

  /**
     * Get layer names visible in the map. All the tested layers are TileWMS (not ImageWMS).
     * This method is used when we are to request the getfeatureinfo.
     * @returns {Array<string>} An array which contains the name of the visible tileWMS layers.
     */
  getVisibleLayerNameList() {
    const visibleLayers = [];
    for (const layer of this.getLayers().getArray()) {
      if (layer.getVisible() && ((layer instanceof BaseTileLayer) || (layer instanceof BaseImageLayer))) {
        visibleLayers.push(layer.getName());
      }
    }
    return visibleLayers;
  }

  /**
     * Get the layer which has the name in parameter.
     * @param {Array<string>} layerNames The layer names.
     * @return {Array<GeomodImageLayer|GeomodTileLayer>} The layers with the parameter name. Empty array if no name.
     */
  getLayerByName(layerNames = []) {
    return this.getLayers().getArray().filter((layer) => layer.getName && layerNames.indexOf(layer.getName()) !== -1);
  }

  /**
     * Get the layer which has the name in parameter.
     * @param {Array<string>} layerNames The layer names.
     * @return {Array<GeomodImageLayer|GeomodTileLayer>} The layers with the parameter name. Empty array if no name.
     */
  getLayerByTag(layerTags = []) {
    return this.getLayers().getArray().filter((layer) => layer.getTag && layerTags.indexOf(layer.getTag()) !== -1);
  }
}

/**
 * Contains layers to group. This object take layers and generates the
 * {@link LayerGroup} on demande.
 */
class LayerToGroup {
  /**
     * @constructor
     * @param {string} groupName The group name.
     */
  constructor(groupName) {
    /** @type {Array<GeomodTileLayer|GeomodImageLayer|string>} */
    this.order_ = [];
    /** @type {Map<string,Array<GeomodTileLayer|GeomodImageLayer>>} */
    this.merge_ = {};
    /** @type {string} */
    this.name_ = groupName;
  }

  /**
     * Creates the layer group and merge layers to merge.
     * @return {LayerGroup} The layer group.
     */
  createGroup() {
    const layers = [];
    for (const layer of this.order_) {
      if (typeof layer === 'string') {
        layers.push(new GeomodMergedLayer({ layers: this.merge_[layer] }));
      } else {
        layers.push(layer);
      }
    }
    return new LayerGroup({ title: this.name_, layers, filter: layers[0].get('filter') });
  }

  /**
     * Add layer in the list.
     * @param {GeomodTileLayer|GeomodImageLayer} layer The layer to add.
     */
  add(layer) {
    const mergeKey = layer.getProperties().merge;
    if (mergeKey) {
      if (!this.merge_[mergeKey]) {
        this.order_.push(mergeKey);
        this.merge_[mergeKey] = [];
      }
      this.merge_[mergeKey].push(layer);
    } else {
      this.order_.push(layer);
    }
  }
}
